'use client';
import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Eye, Edit2, CheckCircle, XCircle, MoreHorizontal } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { BurialApplication } from './BurialManagementContent';

interface BurialTableProps {
  applications: BurialApplication[];
  onSelect: (app: BurialApplication) => void;
}

type SortKey = keyof BurialApplication;

export default function BurialTable({ applications, onSelect }: BurialTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('submittedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const perPage = 8;

  const sorted = [...applications].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === paged.length) setSelected(new Set());
    else setSelected(new Set(paged.map((a) => a.id)));
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={10} className={sortKey === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'} />
      <ChevronDown size={10} className={sortKey === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'} />
    </span>
  );

  const permitColor = (s: string) => {
    if (s === 'Uploaded') return 'text-positive';
    if (s === 'Missing') return 'text-negative';
    return 'text-warning';
  };

  return (
    <div className="card-base overflow-hidden">
      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-secondary border-b border-border slide-up">
          <span className="text-sm font-600 text-primary">{selected.size} selected</span>
          <button className="btn-secondary text-xs py-1"><CheckCircle size={13} /> Approve Selected</button>
          <button className="btn-ghost text-xs py-1 text-negative"><XCircle size={13} /> Reject Selected</button>
          <button onClick={() => setSelected(new Set())} className="btn-ghost text-xs py-1 ml-auto">Clear</button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={selected.size === paged.length && paged.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-primary rounded"
                />
              </th>
              {([
                ['appId', 'App ID'],
                ['deceased', 'Deceased Name'],
                ['idNumber', 'ID Number'],
                ['dateOfDeath', 'Date of Death'],
                ['cemetery', 'Cemetery'],
                ['plot', 'Plot'],
                ['permitStatus', 'Permit'],
                ['scheduledDate', 'Scheduled'],
                ['officer', 'Officer'],
                ['status', 'Status'],
              ] as [SortKey, string][]).map(([key, label]) => (
                <th
                  key={`th-burial-${key}`}
                  onClick={() => toggleSort(key)}
                  className="px-3 py-3 text-left text-[12px] font-600 text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground whitespace-nowrap"
                >
                  {label} <SortIcon col={key} />
                </th>
              ))}
              <th className="px-3 py-3 text-right text-[12px] font-600 text-muted-foreground uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paged.map((app) => (
              <tr
                key={app.id}
                className={`table-row-hover transition-colors ${selected.has(app.id) ? 'bg-secondary/50' : ''}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(app.id)}
                    onChange={() => toggleRow(app.id)}
                    className="w-4 h-4 accent-primary"
                  />
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs text-primary font-600">{app.appId}</span>
                </td>
                <td className="px-3 py-3 font-600 text-foreground whitespace-nowrap">{app.deceased}</td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs text-muted-foreground">{app.idNumber}</span>
                </td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{app.dateOfDeath}</td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{app.cemetery}</td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs font-600">{app.plot}</span>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-xs font-600 ${permitColor(app.permitStatus)}`}>
                    {app.permitStatus}
                  </span>
                </td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{app.scheduledDate}</td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{app.officer}</td>
                <td className="px-3 py-3">
                  <StatusBadge status={app.status} size="sm" />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onSelect(app)}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="View & Approve"
                    >
                      <Eye size={14} className="text-muted-foreground" />
                    </button>
                    <button
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="Edit Application"
                    >
                      <Edit2 size={14} className="text-muted-foreground" />
                    </button>
                    <button
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="More Options"
                    >
                      <MoreHorizontal size={14} className="text-muted-foreground" />
                    </button>
                  </div>
                  {/* Fallback visible icon */}
                  <button
                    onClick={() => onSelect(app)}
                    className="btn-ghost py-1 px-2 text-xs"
                  >
                    <Eye size={13} /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <span className="text-xs text-muted-foreground">
          Showing {Math.min((page - 1) * perPage + 1, sorted.length)}–{Math.min(page * perPage, sorted.length)} of {sorted.length} applications
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 text-xs rounded border transition-colors ${
                page === i + 1
                  ? 'bg-primary text-white border-primary' :'border-border hover:bg-muted'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}